/* eslint-disable camelcase */
import React, { useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Button } from '@farmers-fridge/ui-library';
import CloseIcon from '../icons/CloseIcon';
import FloatingLabel from '../FloatingLabel';
import { sanitizePhoneNumber } from '../../utils/formatPhoneNumber';
// eslint-disable-next-line import/no-cycle
import renderContentfulBody from '../../templates/embeddedEntries/richTextRenderer/renderContentfulBody';
import styles from './SignUpModal.module.css';
import useSiteMetadata from '../../utils/useSiteMetaData';

/** @type {Object<string, string>} */
const PHONE_ERROR_MESSAGES = {
  DUPLICATE_PHONE_NUMBER:
    'Looks like you have an account! Head to your app to earn rewards.',
  INVALID_PHONE_NUMBER: 'Please enter a valid phone number.',
  NOT_ALLOWED_PHONE_NUMBER:
    "Sorry, we can't send SMS messages to this phone number",
  UNKNOWN_ERROR: 'Something went wrong, please try again',
};

/** @type {Object<string, string>} */
const EMAIL_ERROR_MESSAGES = {
  INVALID_EMAIL: 'Please enter a valid email address',
  UNKNOWN_ERROR: 'Something went wrong, please try again',
};

/**
 * @param {object} props
 * @param {(() => void)} props.toggle
 * @param {object} props.data
 * @param {string=} props.data.headline
 * @param {string=} props.data.emailPlaceholder
 * @param {string=} props.data.phonePlacholder
 * @param {string=} props.data.buttonCta
 * @param {object} props.data.image
 * @param {string=} props.data.image.description
 * @param {import('gatsby-plugin-image').IGatsbyImageData=} props.data.image.gatsbyImageData
 * @param {import('gatsby-source-contentful/rich-text.d').RenderRichTextData<import('gatsby-source-contentful/rich-text.d').ContentfulRichTextGatsbyReference>} props.data.helpText
 * @param {import('gatsby-source-contentful/rich-text.d').RenderRichTextData<import('gatsby-source-contentful/rich-text.d').ContentfulRichTextGatsbyReference>} props.data.confirmationText
 * @param {string=} props.data.apiEndpoint
 * @param {string} props.data.contentful_id
 * @param {string=} props.data.triggerDelaySeconds
 * @param {string=} props.data.formVariant
 * @param {string=} props.banner
 *
 */

export default function SignUpModal({ data, toggle, banner }) {
  const { awsApiGatewayUrl } = useSiteMetadata();
  const {
    headline,
    emailPlaceholder,
    phonePlacholder,
    buttonCta,
    image,
    helpText,
    confirmationText,
    apiEndpoint,
    contentful_id,
    triggerDelaySeconds,
    formVariant,
  } = data;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [submitting, setIsSubmitting] = useState(false);
  const [formCompleted, setFormCompleted] = useState(false);

  /** @param {String} value */
  function formatPhoneNumber(value) {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as (xxx) xxx-xxxx
    if (digits.length < 1) return '';
    if (digits.length <= 3) {
      return `(${digits}`;
    }
    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  /** @param {React.FocusEvent<HTMLInputElement >} e */
  function validateEmail(e) {
    const regex = /^((?!\.)[\w-_.]*[^.])(@\w+)(\.\w+(\.\w+)?[^.\W])$/gim;
    const email = e.target.value;
    const isValid = email.match(regex);
    if (!isValid) setEmailError(EMAIL_ERROR_MESSAGES.INVALID_EMAIL);
    else setEmailError('');
  }

  /** @param {React.ChangeEvent<HTMLInputElement >} e */
  function handlePhoneChange(e) {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  const setPersistentCookie = (name, value) => {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 10);
    document.cookie = `${name}=${value}; expires=${expirationDate.toUTCString()}; path=/`;
  };

  function handleCloseModal() {
    toggle();
    if (triggerDelaySeconds) setPersistentCookie(contentful_id, 'true');
  }

  /** @param {string} message */
  async function sha256(message) {
    const encoder = new TextEncoder();
    const encodedMessage = encoder.encode(message);
    const hash = await window.crypto.subtle.digest('SHA-256', encodedMessage);
    const hashArray = Array.from(new Uint8Array(hash));
    const hashHex = hashArray
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    return hashHex;
  }

  async function handleGtmTracking() {
    const formattedEmail = emailAddress.toLowerCase().trim();
    const hashedEmail = await sha256(formattedEmail);
    const sanitizedPhoneNumber = sanitizePhoneNumber(phoneNumber);
    const hashedGooglePhoneNumber = await sha256(`+1${sanitizedPhoneNumber}`);
    const hashedMetaPhoneNumber = await sha256(`1${sanitizedPhoneNumber}`);

    /**
     * @typedef {object} CustomWindowObject
     * @property {Array<any>} [dataLayer]
     * @typedef {Window & CustomWindowObject} WindowWithDataLayer
     * @type {WindowWithDataLayer}
     */
    const win = /** @type {any} */ window;
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({
      event: 'join_now_form_submit',
      variant: formVariant ?? 'no-offer',
      email: hashedEmail,
      google_phone: hashedGooglePhoneNumber,
      meta_phone: hashedMetaPhoneNumber,
    });
  }

  /** @param {React.FormEvent} event */
  async function handleSubmit(event) {
    event.preventDefault();

    let sanitizedPhoneNumber;
    try {
      sanitizedPhoneNumber = sanitizePhoneNumber(phoneNumber);
      setIsPhoneNumberValid(true);
    } catch (e) {
      setPhoneError('INVALID_PHONE_NUMBER');
      setIsPhoneNumberValid(false);
      return;
    }

    setPhoneError('');
    setIsSubmitting(true);
    const phoneNumberWithCountryCode = `+1${sanitizedPhoneNumber}`;

    try {
      await fetch(`${awsApiGatewayUrl}${apiEndpoint ?? '/subscription-form'}`, {
        body: JSON.stringify({
          email: emailAddress,
          phone_number: phoneNumberWithCountryCode,
          form_variant: formVariant,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      }).then(async res => {
        if (res.status === 500) {
          const responseBody = await res.json();
          throw new Error(responseBody.message);
        }

        if (res.status === 400) {
          const responseBody = await res.json();
          throw new Error(responseBody.message);
        }

        if (res.status === 200) {
          setFormCompleted(true);
          setPersistentCookie(contentful_id, 'true');
          await handleGtmTracking();

          if (banner) {
            setPersistentCookie(banner, 'true');
          }
        }
        return res.json();
      });
    } catch (/** @type {any} error */ error) {
      switch (true) {
        case error.message.includes('enter a valid phone number'):
          setPhoneError('INVALID_PHONE_NUMBER');
          break;
        case error.message.includes('already submitted') ||
          error.message.includes('phone number is already tied'):
          setPhoneError('DUPLICATE_PHONE_NUMBER');
          break;
        default:
          setPhoneError('UNKNOWN_ERROR');
      }
    }
    setIsSubmitting(false);
  }

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.modalContainer}>
        <button
          className={styles.closeButton}
          type="button"
          aria-label="Close modal"
          onClick={handleCloseModal}
        >
          <CloseIcon className={styles.navIcon} />
        </button>
        {!formCompleted && <h2 className={styles.headline}>{headline}</h2>}
        {image.gatsbyImageData && (
          <GatsbyImage
            alt={image.description ?? ''}
            image={image.gatsbyImageData}
            loading="lazy"
            className={styles.image}
          />
        )}
        <div className={styles.inputWrapper}>
          {!formCompleted && (
            <>
              <h2 className={styles.headlineDesktop}>{headline}</h2>
              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <FloatingLabel
                  className={styles.labelWrapper}
                  error={
                    phoneError
                      ? PHONE_ERROR_MESSAGES[phoneError] ||
                        PHONE_ERROR_MESSAGES.UNKNOWN_ERROR
                      : undefined
                  }
                >
                  <input
                    type="tel"
                    id="signUpModalPhone"
                    name="phoneNumber"
                    aria-label={phonePlacholder}
                    autoComplete="tel-national"
                    value={
                      isPhoneNumberValid
                        ? formatPhoneNumber(phoneNumber)
                        : phoneNumber
                    }
                    onChange={handlePhoneChange}
                    maxLength={14}
                  />
                </FloatingLabel>
                <FloatingLabel
                  className={styles.labelWrapper}
                  error={
                    emailError
                      ? EMAIL_ERROR_MESSAGES.INVALID_EMAIL ||
                        EMAIL_ERROR_MESSAGES.UNKNOWN_ERROR
                      : undefined
                  }
                >
                  <input
                    type="email"
                    id="signUpModalEmail"
                    name="emailAddress"
                    aria-label={emailPlaceholder}
                    autoComplete="email"
                    onChange={e => {
                      setEmailError('');
                      setEmailAddress(e.target.value);
                    }}
                    value={emailAddress}
                    onBlur={validateEmail}
                  />
                </FloatingLabel>
                <Button
                  buttonType="primary"
                  className={styles.button}
                  disabled={!phoneNumber}
                  loading={submitting}
                  type="submit"
                >
                  {buttonCta}
                </Button>

                {helpText && (
                  <div className={styles.helpText}>
                    {renderContentfulBody(helpText)}
                  </div>
                )}
              </form>
            </>
          )}
          {formCompleted && confirmationText && (
            <div>{renderContentfulBody(confirmationText)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
